import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/http/client";
import { patientKeys } from "@features/patients/queries";
import type { EvaluationEnvelope, EvaluationQuery, PaginatedResponse } from "@/types/evaluation";

export const evaluationKeys = {
  all: ["evaluations"] as const,
  list: (q: EvaluationQuery) => [...evaluationKeys.all, "list", q] as const,
  byId: (id: number) => [...evaluationKeys.all, "byId", id] as const,
};

function toQueryString(q: EvaluationQuery): string {
  const p = new URLSearchParams();
  if (q.from)   p.set("from", q.from);
  if (q.to)     p.set("to", q.to);
  if (q.search) p.set("search", q.search);
  if (q.page)   p.set("page", String(q.page));
  if (q.limit)  p.set("limit", String(q.limit));
  const s = p.toString();
  return s ? "?" + s : "";
}

export function useEvaluations(query: EvaluationQuery = {}) {
  return useQuery({
    queryKey: evaluationKeys.list(query),
    queryFn: () => api.get<PaginatedResponse<EvaluationEnvelope>>(`/evaluations${toQueryString(query)}`),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  // Al crear/editar una valoración el paciente pasa de 'pendiente' a 'valorado'
  // en scheduling — invalidamos también patientKeys para refrescar Pendientes.
  qc.invalidateQueries({ queryKey: evaluationKeys.all });
  qc.invalidateQueries({ queryKey: patientKeys.all });
}

export function useCreateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (evaluation: EvaluationEnvelope) =>
      api.post<EvaluationEnvelope>("/evaluations", evaluation),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, evaluation }: { id: number; evaluation: EvaluationEnvelope }) =>
      api.patch<EvaluationEnvelope>(`/evaluations/${id}`, evaluation),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: true; id: number }>(`/evaluations/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}
